import {Component, forwardRef, OnDestroy} from '@angular/core';
import {CommonModule} from '@angular/common';
import {DlCheckboxComponent} from '../dl-checkbox/dl-checkbox.component';
import {DlRadioComponent} from '../dl-radio/dl-radio.component';
import {FormsModule} from '@angular/forms';
import {Subscription} from 'rxjs';
import {
  AbstractControl,
  ControlValueAccessor,
  FormArray,
  FormBuilder,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';

import {
  AUDIO_CODEC_LABELS,
  AUDIO_CODECS,
  AUDIO_MODES,
  AUTO_AUDIO_LAYOUTS,
  AUTO_AUDIO_SAMPLERATE,
  AUDIO_MODE_COLUMNS,
  AudioMode,
  audioCodecAllowedAt,
  normaliseAudioLayout,
  DEFAULT_GOP_IN_SECONDS,
  DEFAULT_GOPS_PER_SEGMENT,
  AudioRow,
  LadderAudioLayout,
  audioModeWritesLayouts,
  isCustomAudioMode,
  defaultAudioRows,
  layoutName,
  BITRATE_PATTERN,
  encoderParamsToText,
  parseEncoderParams,
  CUSTOM_PRESET_ID,
  AUDIO_DRM_TIER,
  DEFAULT_DRM_STREAMTYPES,
  DRM_STREAMTYPES,
  DrmStreamType,
  EncodingLadder,
  LADDER_CODECS,
  LADDER_PRESETS,
  LadderAudioRendition,
  LADDER_OUTPUTS,
  LadderCodec,
  LadderDrmTier,
  LadderGroup,
  LadderRendition,
  PIXEL_FORMATS,
  profileCarriesPixfmt,
  stableStringify,
} from './encoding-ladder.model';

/**
 * Editor for `format_specific_data.encoding_ladder` of the `vod-default-cmaf-abr` template.
 *
 * Writes `null` while the ladder is switched off, which drops the key entirely so the template
 * renders its own default. That is not the same as picking the "Default" preset: the preset spells
 * the same ladder out explicitly, which is what the operator sees and can then edit.
 *
 * The validation here deliberately mirrors what the template raises on, because the ladder is the
 * one part of `format_specific_data` that is validated at render time rather than silently
 * ignored -- a mistake caught here is a job that never reaches the encoder in a broken state.
 */
@Component({
  selector: 'app-encoding-ladder',
  templateUrl: './encoding-ladder.component.html',
  // castlabs-ui sets font-weight 700 on the whole `.card.cl-tight .card-header`, and has no
  // fw-normal; only the card's name is meant to be strong, so the overview beside it resets it.
  styles: ['.ladder-card-overview { font-weight: 400; }'],
  imports: [CommonModule, FormsModule, ReactiveFormsModule, DlCheckboxComponent, DlRadioComponent],
  providers: [
    {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => EncodingLadderComponent), multi: true},
    {provide: NG_VALIDATORS, useExisting: forwardRef(() => EncodingLadderComponent), multi: true},
  ],
})
export class EncodingLadderComponent implements ControlValueAccessor, OnDestroy {
  readonly presets = LADDER_PRESETS;
  readonly codecs = LADDER_CODECS;
  readonly codecIds = Object.keys(LADDER_CODECS) as LadderCodec[];
  readonly streamTypes = DRM_STREAMTYPES;
  readonly audioCodecs = AUDIO_CODECS;
  readonly audioModes = AUDIO_MODES;
  /** Only "Passthrough all" is selectable; Custom is where an edit lands, never a choice. */
  readonly audioStartModes = AUDIO_MODES.filter(m => !isCustomAudioMode(m.id));
  readonly autoAudioLayouts = AUTO_AUDIO_LAYOUTS;
  readonly autoAudioSamplerate = AUTO_AUDIO_SAMPLERATE;
  readonly pixelFormats = PIXEL_FORMATS;

  presetId: string = LADDER_PRESETS[0].id;
  /**
   * The preset last picked by the user: what each card's "Changed" badge compares against. Unlike
   * `presetId` it survives an edit, since that is exactly when the badge is wanted.
   */
  basePresetId: string = LADDER_PRESETS[0].id;
  form: FormGroup;

  private disabled = false;
  /** Set while the form is being rebuilt, so the rebuild does not report itself as an edit. */
  private loading = false;
  /** Every subscription on the *current* form, dropped whenever the form is rebuilt. */
  private subscriptions = new Subscription();

  changed: (value: EncodingLadder | null) => void = () => {};
  touched: () => void = () => {};

  constructor(private fb: FormBuilder) {
    this.form = this._buildForm(LADDER_PRESETS[0].ladder);
    this._watch();
  }

  // ── Template accessors ─────────────────────────────────────────────────────

  get groups(): FormArray { return this.form.get('groups') as FormArray; }

  get drmTiers(): FormArray { return this.form.get('drm_streamtypes') as FormArray; }

  get audioRows(): FormArray { return this.form.get('audio_rows') as FormArray; }

  /** Which columns the current mode writes; the others render disabled. */
  audioColumnApplies(column: keyof AudioRow): boolean {
    return AUDIO_MODE_COLUMNS[this.form.get('audio_mode').value as AudioMode].includes(column);
  }

  readonly layoutName = layoutName;

  renditionsOf(group: AbstractControl): FormArray { return group.get('renditions') as FormArray; }

  /** Codecs not already taken by another group -- the template refuses two groups on one codec. */
  availableCodecs(groupIndex: number): LadderCodec[] {
    const taken = this.groups.controls
      .filter((_, i) => i !== groupIndex)
      .map(g => g.get('codec').value);
    return this.codecIds.filter(c => !taken.includes(c));
  }

  canAddGroup(): boolean { return this.groups.length < this.codecIds.length; }

  /**
   * The Source card's header overview, so the selection reads without opening it. Upscaling only
   * means something with video, so an audio-only ladder leaves it out, as the card does.
   */
  get sourceSummary(): string {
    if (this.audioOnly) {
      return 'Audio only';
    }
    return `Audio/Video · ${this.form.get('exceed_input_resolution').value ? 'Upscale' : 'No upscale'}`;
  }

  /**
   * Whether the Source card differs from the preset the user last picked (`basePreset`).
   * Every card works this way: it compares only its own settings.
   */
  get sourceChanged(): boolean {
    const base = this._baseSource();
    return this.audioOnly !== base.audioOnly
      || !!this.form.get('exceed_input_resolution').value !== base.exceedInputResolution;
  }

  /** The preset the user last picked, or the first one for a ladder that arrived matching none. */
  private get basePreset() {
    return LADDER_PRESETS.find(p => p.id === this.basePresetId) ?? LADDER_PRESETS[0];
  }

  /** What the base preset says about the Source card's two settings. */
  private _baseSource(): {audioOnly: boolean; exceedInputResolution: boolean} {
    const ladder = this.basePreset.ladder;
    return {
      audioOnly: EncodingLadderComponent._groupsOf(ladder).length === 0,
      exceedInputResolution: !!ladder.exceed_input_resolution,
    };
  }

  /** Whether the GOP card differs from the preset the user last picked. */
  get gopChanged(): boolean {
    const base = this._baseGop();
    return numberOrNull(this.form.get('gop_in_seconds').value) !== base.gopInSeconds
      || numberOrNull(this.form.get('gops_per_segment').value) !== base.gopsPerSegment;
  }

  /** What the base preset says about the GOP card's two settings, with the template's defaults filled in. */
  private _baseGop(): {gopInSeconds: number; gopsPerSegment: number} {
    const ladder = normaliseLadder(this.basePreset.ladder);
    return {gopInSeconds: ladder.gop_in_seconds, gopsPerSegment: ladder.gops_per_segment};
  }

  /**
   * The Video card's header overview. One group says what it encodes with -- codec, encoder preset,
   * rung count; several say which codecs the ladder switches between and the rungs in all.
   */
  get videoSummary(): string {
    const groups = this.groups.getRawValue() as {codec: LadderCodec; preset: string | null; renditions: unknown[]}[];
    if (!groups.length) {
      return '';
    }
    const rungs = groups.reduce((sum, g) => sum + g.renditions.length, 0);
    const count = `${rungs} ${rungs === 1 ? 'rendition' : 'renditions'}`;
    const label = (g: {codec: LadderCodec}) => LADDER_CODECS[g.codec]?.label ?? g.codec;
    return groups.length === 1
      ? [label(groups[0]), groups[0].preset, count].filter(Boolean).join(' · ')
      : `${groups.map(label).join(' + ')} · ${count}`;
  }

  /**
   * Whether the Video card differs from the preset the user last picked: the codec groups of the
   * built ladder against the preset's, in the one shape both are emitted in.
   */
  get videoChanged(): boolean {
    return stableStringify(this._build().groups) !== stableStringify(normaliseLadder(this.basePreset.ladder).groups);
  }

  /**
   * The Audio card's header overview: the mode, the codecs the rows deliver and how many renditions
   * that is -- the same shape as the Video card's.
   */
  get audioSummary(): string {
    const rows = this.audioRows.getRawValue() as AudioRow[];
    if (!rows.length) {
      return 'Incomplete';
    }
    const mode = this.audioModes.find(m => m.id === this.form.get('audio_mode').value)?.label ?? 'Custom';
    const codecs = [...new Set(rows.map(r => r.codec || 'aac'))];
    return `${mode} · ${this.codecLabels(codecs)} · ${rows.length} ${rows.length === 1 ? 'rendition' : 'renditions'}`;
  }

  /**
   * Whether the Audio card differs from the preset the user last picked.
   *
   * Compared on what the job would be sent -- the two audio keys of the built ladder against the
   * same keys of the preset in the same shape -- not on the rows, so a layout edited back onto the
   * template's defaults is not a change, exactly as `_matchPreset` treats it.
   */
  get audioChanged(): boolean {
    const audioKeys = (l: EncodingLadder) => stableStringify({
      audio_layouts: l.audio_layouts ?? null,
      audio_renditions: l.audio_renditions ?? null,
    });
    return audioKeys(this._build()) !== audioKeys(normaliseLadder(this.basePreset.ladder));
  }

  /**
   * The DRM card's header overview: the width tiers the way an operator reads them, "SD up to 864,
   * then HD". AUDIO is always there and never a choice, so it is left out -- except when it is all
   * there is.
   */
  get drmSummary(): string {
    if (this.audioOnly) {
      return 'AUDIO only';
    }
    const tiers = this.drmTiers.getRawValue() as {streamtype: string; max_width: number | null}[];
    return tiers.map(t => numberOrNull(t.max_width) === null ? t.streamtype : `${t.streamtype} \u2264 ${t.max_width}`)
      .join(' \u00b7 ');
  }

  /**
   * Whether the width tiers differ from the preset the user last picked. Read off the tier rows
   * rather than the built ladder: an audio-only ladder drops them from what it sends, which is no
   * edit of theirs.
   */
  get drmChanged(): boolean {
    const tiers = (this.drmTiers.getRawValue() as {streamtype: string; max_width: unknown}[])
      .map(t => ({streamtype: t.streamtype, max_width: numberOrNull(t.max_width)}));
    const base = normaliseLadder(this.basePreset.ladder).drm_streamtypes
      .filter(t => t.streamtype !== AUDIO_DRM_TIER.streamtype);
    return stableStringify(tiers) !== stableStringify(base);
  }

  /** The audio-only Outputs card's header overview: which manifests the audio is packaged into. */
  get outputsSummary(): string {
    const picked = [
      ...(this.form.get('ladder_out_dash').value ? ['DASH'] : []),
      ...(this.form.get('ladder_out_hls').value ? ['HLS'] : []),
    ];
    return picked.length ? picked.join(' + ') : 'None';
  }

  /** Whether the audio-only outputs differ from the ladder's default of both manifests. */
  get outputsChanged(): boolean {
    return !this.form.get('ladder_out_dash').value || !this.form.get('ladder_out_hls').value;
  }

  // Whether a card holds an error. Every card starts collapsed, so a blocker inside one would
  // otherwise be invisible: its header says so instead. Each reads the control the card owns, which
  // is also what `validate()` rolls up, so the badges and the submit gate cannot disagree.

  /** GOP: either number empty or below 1. */
  get gopInvalid(): boolean {
    return this.form.get('gop_in_seconds').invalid || this.form.get('gops_per_segment').invalid;
  }

  /** Video: the group list and everything inside every group and rendition. */
  get videoInvalid(): boolean { return this.groups.invalid; }

  /** Audio-only Outputs: the empty group list's own rule, that at least one manifest is picked. */
  get outputsInvalid(): boolean { return this.groups.invalid; }

  /** Audio: the rendition table and the rules across its rows. */
  get audioInvalid(): boolean { return this.audioRows.invalid; }

  /** DRM: the tier list and its order and bounds. */
  get drmInvalid(): boolean { return this.drmTiers.invalid; }

  /** Whether the ladder currently declares no video at all -- vtkpy's explicit `"groups": []`. */
  get audioOnly(): boolean { return this.groups.length === 0; }

  /**
   * Switch between a video ladder and an audio-only one.
   *
   * Audio-only is `"groups": []` plus a ladder-level `outputs`, since there is no group left to
   * carry it -- the exact shape vtkpy 1882f32e added for a source with no video track. Turning it
   * off restores a single default H.264 group rather than leaving the operator to add one back by
   * hand; `addGroup` already picks the first free codec, which on an empty list is the same thing.
   */
  setAudioOnly(on: boolean): void {
    if (on === this.audioOnly) {
      return;
    }
    if (on) {
      while (this.groups.length) { this.groups.removeAt(0); }
    } else {
      this.addGroup();
    }
    // drmTiersValidator also reads 'groups' (an audio-only ladder needs no width tier at all),
    // and a sibling FormArray's own mutation does not bubble sideways into this one's cache.
    this.drmTiers.updateValueAndValidity({emitEvent: false});
    this.touched();
    this._emit();
  }

  /** AV1 has no usable two-pass VBR, so the template rejects `two_pass` for it outright. */
  supportsTwoPass(group: AbstractControl): boolean {
    return LADDER_CODECS[group.get('codec').value as LadderCodec]?.twoPass ?? false;
  }

  profilesFor(group: AbstractControl): string[] {
    return LADDER_CODECS[group.get('codec').value as LadderCodec]?.profiles ?? [];
  }

  presetsFor(group: AbstractControl): string[] {
    return LADDER_CODECS[group.get('codec').value as LadderCodec]?.presets ?? [];
  }

  /** The encoder's top CRF, for the inputs' `max` -- the validators enforce the same bound. */
  crfMax(group: AbstractControl): number {
    return LADDER_CODECS[group.get('codec').value as LadderCodec]?.crfMax ?? 51;
  }

  /** H.264 takes no `encoder_params`: the template only appends them to x265's and SVT-AV1's strings. */
  supportsEncoderParams(group: AbstractControl): boolean {
    return LADDER_CODECS[group.get('codec').value as LadderCodec]?.encoderParams ?? false;
  }

  // ── User actions ───────────────────────────────────────────────────────────

  applyPreset(id: string): void {
    const preset = LADDER_PRESETS.find(p => p.id === id);
    if (!preset) {
      this.presetId = CUSTOM_PRESET_ID;
      return;
    }
    this._load(preset.ladder);
    this.presetId = preset.id;
    this.basePresetId = preset.id;
    this.touched();
    this._emit();
  }

  /** The preset a hand-edited ladder is no longer equal to, for the "Custom" note. */
  get selectedPreset() {
    return LADDER_PRESETS.find(p => p.id === this.presetId) ?? null;
  }

  addGroup(): void {
    const free = this.availableCodecs(-1);
    if (!free.length) {
      return;
    }
    const codec = free[0];
    this.groups.push(this._groupForm({
      codec,
      outputs: ['dash', 'hls'],
      crf: 23,
      two_pass: false,
      profile: LADDER_CODECS[codec].defaultProfile,
      preset: LADDER_CODECS[codec].defaultPreset,
      pixfmt: LADDER_CODECS[codec].defaultPixfmt,
      renditions: [{width: 1280, max_bitrate: '4000k'}],
    }));
  }

  removeGroup(index: number): void { this.groups.removeAt(index); }

  addRendition(group: AbstractControl): void {
    this.renditionsOf(group).push(
      this._renditionForm({width: 1280, max_bitrate: '4000k'}), {emitEvent: false});
    this._announceRenditionChange(group as FormGroup);
  }

  removeRendition(group: AbstractControl, index: number): void {
    this.renditionsOf(group).removeAt(index, {emitEvent: false});
    this._announceRenditionChange(group as FormGroup);
  }

  addDrmTier(): void {
    // Widen the list downwards: the entry that was unbounded keeps that role, the new one slots in
    // above it, so the "last tier is unbounded" rule survives an add without further editing.
    const last = this.drmTiers.at(this.drmTiers.length - 1);
    const nextType = DRM_STREAMTYPES.find(t => !this._declaredTiers().includes(t));
    if (!nextType) {
      return;
    }
    if (last) {
      last.get('max_width').setValue(1920);
    }
    this.drmTiers.push(this._drmTierForm({streamtype: nextType, max_width: null}));
  }

  removeDrmTier(index: number): void {
    this.drmTiers.removeAt(index);
    const last = this.drmTiers.at(this.drmTiers.length - 1);
    if (last) {
      last.get('max_width').setValue(null);
    }
  }

  setAudioMode(mode: AudioMode): void {
    const previous = this.form.get('audio_mode').value as AudioMode;
    this.form.get('audio_mode').setValue(mode, {emitEvent: false});
    if (mode !== previous) {
      // The columns a mode writes differ, so carrying rows across would leave half of them
      // meaningless. Start each mode from its own defaults.
      this.resetAudioRows();
    }
    this.touched();
    this._emit();
  }

  isCustomAudio(): boolean { return isCustomAudioMode(this.form.get('audio_mode').value); }

  /**
   * Editing a row makes the set the operator's own, so the mode follows -- exactly as editing a
   * ladder turns a preset into Custom. The written key is preserved: a layout policy stays a
   * layout policy, because the two are not interchangeable.
   */
  private _markAudioEdited(): void {
    const mode = this.form.get('audio_mode').value as AudioMode;
    if (mode === 'passthrough_all' && !this.audioRowsAreDefault()) {
      this.form.get('audio_mode').setValue('custom_layouts', {emitEvent: false});
    } else if (mode === 'custom_layouts' && this.audioRowsAreDefault()) {
      // Edited back onto the defaults: say so rather than claiming a custom set.
      this.form.get('audio_mode').setValue('passthrough_all', {emitEvent: false});
    }
  }

  /** True while the rows still match what the template derives on its own. */
  audioRowsAreDefault(): boolean {
    return stableStringify(this._buildAudioLayouts())
      === stableStringify(AUTO_AUDIO_LAYOUTS.map(l => normaliseAudioLayout(l)));
  }

  /**
   * The codecs a row may pick, given its own channel count.
   *
   * HE-AACv2 is defined for stereo only, so it is left out of a mono or multichannel row rather
   * than offered and then rejected at render time. A row that already holds it keeps it in the
   * list, or changing the channel count would blank the select instead of flagging the clash --
   * `audioRowsValidator` is what says so.
   */
  audioCodecsFor(row: AbstractControl): string[] {
    const channels = numberOrNull(row.get('channels').value);
    const chosen = row.get('codec').value;
    return this.audioCodecs.filter(c => c === chosen || audioCodecAllowedAt(c, channels));
  }

  /**
   * The GOP card's header overview. Says what the two numbers mean together -- where the keyframes
   * fall and how long a segment is -- rather than repeating them, since the second is the product.
   */
  get gopSummary(): string {
    const gop = numberOrNull(this.form.get('gop_in_seconds').value);
    const segment = this.segmentDuration();
    return gop === null || segment === null ? 'Incomplete' : `Keyframe every ${gop} s · ${segment} s segments`;
  }

  /** What the packager is actually handed: a segment is a whole number of GOPs. */
  segmentDuration(): number | null {
    const gop = numberOrNull(this.form.get('gop_in_seconds').value);
    const per = numberOrNull(this.form.get('gops_per_segment').value);
    return gop === null || per === null ? null : gop * per;
  }

  /** "aac", "eac3" -> "AAC + E-AC-3", so the table reads the way an operator says it. */
  codecLabels(codecs: string[]): string {
    return codecs.map(c => AUDIO_CODEC_LABELS[c] ?? c.toUpperCase()).join(' + ');
  }

  audioModeDescription(): string {
    const mode = this.form.get('audio_mode').value;
    return this.audioModes.find(m => m.id === mode)?.description ?? '';
  }

  // ── ControlValueAccessor / Validator ───────────────────────────────────────

  writeValue(value: EncodingLadder | null): void {
    // Nothing stored is not a third state: the first preset renders exactly what the template
    // renders with no ladder key at all (verified against the template, not assumed), so showing
    // it selected is honest rather than a guess. The PO form seeds the control with it for that
    // reason, which is what keeps the visible selection and the submitted ladder the same thing.
    const ladder = value && typeof value === 'object' ? value : LADDER_PRESETS[0].ladder;
    this._load(ladder);
    this.presetId = EncodingLadderComponent._matchPreset(this._build());
    // A stored ladder matching a preset is that preset; one matching none has no preset to compare
    // with, so the badge falls back to the first, which is what the template renders by default.
    this.basePresetId = this.presetId === CUSTOM_PRESET_ID ? LADDER_PRESETS[0].id : this.presetId;
  }

  ngOnDestroy(): void { this.subscriptions.unsubscribe(); }

  registerOnChange(fn: (value: EncodingLadder | null) => void): void { this.changed = fn; }

  registerOnTouched(fn: () => void): void { this.touched = fn; }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    if (isDisabled) {
      this.form.disable({emitEvent: false});
    } else {
      this.form.enable({emitEvent: false});
      this._syncCodecConstraints();
    }
  }

  validate(): ValidationErrors | null {
    if (this.disabled) {
      return null;
    }
    return this.form.valid ? null : {invalidEncodingLadder: true};
  }

  // ── Form construction ──────────────────────────────────────────────────────

  private _buildForm(ladder: EncodingLadder): FormGroup {
    const groups = EncodingLadderComponent._groupsOf(ladder);
    // Only meaningful for an audio-only ladder (no group left to carry 'outputs' itself), but
    // seeded regardless so a ladder switched into audio-only starts from something sensible.
    const ladderOutputs = ladder.outputs ?? ['dash', 'hls'];
    const form = this.fb.group({
      // Both are ladder-level because the packager takes one segment duration for the whole job.
      // A ladder stored before vtkpy 66d5b4b9 carries the GOP on its first group instead, so it is
      // lifted off there -- dropping it would silently re-time every rendition of an existing PO.
      gop_in_seconds: [
        ladder.gop_in_seconds ?? (groups[0] as any)?.gop_in_seconds ?? DEFAULT_GOP_IN_SECONDS,
        [Validators.required, Validators.min(1)],
      ],
      gops_per_segment: [
        ladder.gops_per_segment ?? DEFAULT_GOPS_PER_SEGMENT,
        [Validators.required, Validators.min(1)],
      ],
      exceed_input_resolution: [ladder.exceed_input_resolution ?? false],
      // UI-only: which of the three shapes audio_renditions is written in. An empty list is not
      // one of them -- the template rejects it -- so 'custom' always carries at least one step.
      audio_mode: [EncodingLadderComponent._audioModeOf(ladder)],
      // One table for all three modes: a row is one output rendition, which is already the shape of an
      // audio_renditions entry and what an audio_layouts entry collapses to per codec.
      audio_rows: this.fb.array(
        EncodingLadderComponent._rowsFor(ladder).map(r => this._audioRowForm(r)),
        audioRowsValidator,
      ),
      // Width tiers only. The AUDIO tier is not a choice -- it is prepended on the way out (see
      // _build) -- so a ladder arriving from a stored PO has it filtered off here rather than
      // becoming a row an operator could reorder or delete.
      drm_streamtypes: this.fb.array(
        (ladder.drm_streamtypes ?? DEFAULT_DRM_STREAMTYPES)
          .filter(t => t.streamtype !== AUDIO_DRM_TIER.streamtype)
          .map(t => this._drmTierForm(t)),
        drmTiersValidator,
      ),
      // Ladder-level outputs, read only while 'groups' is empty -- see groupsValidator and _build.
      ladder_out_dash: [ladderOutputs.includes('dash')],
      ladder_out_hls: [ladderOutputs.includes('hls')],
      groups: this.fb.array(groups.map(g => this._groupForm(g)), groupsValidator),
    });
    // groupsValidator reads these two when the ladder is audio-only, so it has to re-run when
    // either moves -- a sibling's own valueChanges does not bubble sideways into the array's
    // cached validity the way it bubbles up to the parent form.
    ['ladder_out_dash', 'ladder_out_hls'].forEach(name => this.subscriptions.add(
      form.get(name).valueChanges.subscribe(
        () => (form.get('groups') as FormArray).updateValueAndValidity({emitEvent: false})),
    ));
    // 'groups' (and, below, 'drm_streamtypes') computed its *first* validity while still
    // unattached -- fb.array() runs the validator inside its own constructor, before fb.group()
    // has set its parent -- so a validator's very first read of control.parent above sees
    // undefined. Refresh both now that the group actually holds what they read off each other.
    (form.get('groups') as FormArray).updateValueAndValidity({emitEvent: false});
    (form.get('drm_streamtypes') as FormArray).updateValueAndValidity({emitEvent: false});
    return form;
  }

  private _groupForm(group: LadderGroup): FormGroup {
    const codec = (group.codec === ('hevc' as LadderCodec) ? 'h265' : group.codec) as LadderCodec;
    const meta = LADDER_CODECS[codec] ?? LADDER_CODECS.h264;
    const twoPass = !!group.two_pass && meta.twoPass;
    const form = this.fb.group({
      codec: [codec, Validators.required],
      out_dash: [(group.outputs ?? []).includes('dash')],
      out_hls: [(group.outputs ?? []).includes('hls')],
      two_pass: [{value: twoPass, disabled: !meta.twoPass}],
      crf: [group.crf ?? null, [crfRequiredUnlessTwoPass, Validators.min(0), crfWithinCodecRange]],
      profile: [group.profile ?? null],
      preset: [group.preset ?? null],
      pixfmt: [group.pixfmt ?? meta.defaultPixfmt],
      encoder_params: [encoderParamsToText(group.encoder_params), encoderParamsValidator],
      renditions: this.fb.array(
        (group.renditions ?? []).map(r => this._renditionForm(r)),
        (c: AbstractControl) => (c as FormArray).length ? null : {noRenditions: true},
      ),
    }, {validators: groupValidator});

    this.subscriptions.add(form.get('codec').valueChanges.subscribe((next: LadderCodec) => {
      const nextMeta = LADDER_CODECS[next];
      const twoPassCtrl = form.get('two_pass');
      if (nextMeta.twoPass) {
        twoPassCtrl.enable({emitEvent: false});
      } else {
        twoPassCtrl.setValue(false, {emitEvent: false});
        twoPassCtrl.disable({emitEvent: false});
      }
      // Profile/preset are encoder vocabulary, not shared settings: carrying x264's textual preset
      // over to libsvtav1 would fail to open the encoder, so reset both to the new codec's default.
      form.get('profile').setValue(nextMeta.defaultProfile, {emitEvent: false});
      form.get('preset').setValue(nextMeta.defaultPreset, {emitEvent: false});
      // pixfmt too: the codecs do not share a default. Carrying yuv420p onto HEVC would quietly
      // drop it to 8-bit, and carrying yuv420p10le onto H.264 refuses to open the encoder.
      form.get('pixfmt').setValue(nextMeta.defaultPixfmt, {emitEvent: false});
      // And the tuning: x265's and SVT-AV1's parameter names do not overlap.
      form.get('encoder_params').setValue('', {emitEvent: false});
      // A codec without VBR (AV1) refuses avg_bitrate on every rung, so the averages carried over
      // would only stand there as errors to clear by hand. Rung CRFs stay: they mean the same thing.
      if (!nextMeta.twoPass) {
        (form.get('renditions') as FormArray).controls.forEach(r => r.get('avg_bitrate').setValue(null, {emitEvent: false}));
      }
      this._syncCrfEnabled(form);
      this._refreshConditionalValidity(form);
    }));

    this.subscriptions.add(
      form.get('two_pass').valueChanges.subscribe(() => {
        this._syncCrfEnabled(form);
        this._refreshConditionalValidity(form);
      }),
    );
    // groupValidator reads profile and pixfmt, so it has to re-run when either moves.
    ['profile', 'pixfmt'].forEach(name => this.subscriptions.add(
      form.get(name).valueChanges.subscribe(() => form.updateValueAndValidity({emitEvent: false})),
    ));

    this._syncCrfEnabled(form);
    return form;
  }

  /**
   * CRF applies only to a single-pass group.
   *
   * Disabled rather than readonly: a two-pass group's CRF is not merely uneditable, it is not sent
   * at all (`_build` drops it, and the template never emits `-crf`). A readonly input painted
   * identically to a live one and went on displaying whatever number was last typed, which read as
   * "this encode targets CRF 23" when nothing of the kind would happen. Disabling also clears the
   * value from `form.value`, so nothing downstream can pick it up by accident -- `_build` reads
   * `getRawValue()` where it genuinely needs a disabled control's contents.
   */
  private _syncCrfEnabled(group: FormGroup): void {
    const crf = group.get('crf');
    if (group.get('two_pass').value) {
      crf.disable({emitEvent: false});
    } else {
      crf.enable({emitEvent: false});
    }
  }

  /**
   * One rung. `max_bitrate` is optional: left blank, the rung is uncapped and states its own
   * `avg_bitrate` or `crf` instead (`renditionRateValidator`).
   */
  private _renditionForm(rendition: LadderRendition): FormGroup {
    const form = this.fb.group({
      width: [rendition.width ?? null, [Validators.required, Validators.min(1)]],
      avg_bitrate: [rendition.avg_bitrate ?? null, [avgBitrateRequiredWhenTwoPass, avgBitrateNeedsVbr, bitratePattern]],
      max_bitrate: [rendition.max_bitrate ?? null, [bitratePattern]],
      crf: [rendition.crf ?? null, [Validators.min(0), crfWithinCodecRange]],
      min_bitrate: [rendition.min_bitrate ?? null, [bitratePattern]],
      bufsize: [rendition.bufsize ?? null, [bitratePattern]],
    }, {validators: renditionRateValidator});
    // A rung's own crf excuses it from avg_bitrate inside a two-pass group, and a rung naming its
    // own target excuses the group from its crf -- so both re-run when either target moves.
    ['crf', 'avg_bitrate'].forEach(name => this.subscriptions.add(form.get(name).valueChanges.subscribe(() => {
      form.get('avg_bitrate').updateValueAndValidity({emitEvent: false});
      form.parent?.parent?.get('crf')?.updateValueAndValidity({emitEvent: false});
    })));
    return form;
  }

  /** One output rendition, in every mode. Columns the mode does not write are disabled, not removed. */
  private _audioRowForm(row: AudioRow): FormGroup {
    return this.fb.group({
      channels: [row.channels, [Validators.min(1)]],
      codec: [row.codec || 'aac'],
      bitrate: [row.bitrate, [bitratePattern]],
      samplerate: [row.samplerate],
      display: [row.display],
      required: [!!row.required],
      allow_downmix: [!!row.allow_downmix],
    });
  }

  addAudioRow(): void {
    const mode = this.form.get('audio_mode').value as AudioMode;
    this.audioRows.push(this._audioRowForm(
      defaultAudioRows(audioModeWritesLayouts(mode) ? 'passthrough_all' : 'custom_renditions')[0]));
  }

  removeAudioRow(index: number): void { this.audioRows.removeAt(index); }

  resetAudioRows(): void {
    const mode = this.form.get('audio_mode').value as AudioMode;
    while (this.audioRows.length) { this.audioRows.removeAt(0); }
    defaultAudioRows(mode).forEach(r => this.audioRows.push(this._audioRowForm(r)));
    if (isCustomAudioMode(mode) && audioModeWritesLayouts(mode)) {
      this.form.get('audio_mode').setValue('passthrough_all', {emitEvent: false});
    }
  }



  private _drmTierForm(tier: LadderDrmTier): FormGroup {
    return this.fb.group({
      streamtype: [tier.streamtype, Validators.required],
      max_width: [tier.max_width ?? null],
    });
  }

  // ── Wiring ─────────────────────────────────────────────────────────────────

  private _watch(): void {
    this.subscriptions.add(this.form.valueChanges.subscribe(() => {
      if (this.loading) {
        return;
      }
      // A hand edit takes the ladder off its preset -- the dropdown is a starting point, not a
      // constraint, and claiming a preset the ladder no longer matches would be a lie. Editing
      // back onto a preset re-selects it, so the label always describes what is actually there.
      this.presetId = EncodingLadderComponent._matchPreset(this._build());
      this._markAudioEdited();
      this.touched();
      this._emit();
    }));
  }

  private _load(ladder: EncodingLadder): void {
    this.loading = true;
    this.subscriptions.unsubscribe();
    this.subscriptions = new Subscription();
    this.form = this._buildForm(ladder);
    if (this.disabled) {
      this.form.disable({emitEvent: false});
    }
    this._watch();
    this.loading = false;
    this._syncCodecConstraints();
  }

  /** Re-run the validators whose outcome depends on a sibling control rather than their own value. */
  private _refreshConditionalValidity(group: FormGroup): void {
    group.get('crf').updateValueAndValidity({emitEvent: false});
    group.get('encoder_params').updateValueAndValidity({emitEvent: false});
    (group.get('renditions') as FormArray).controls.forEach(r => {
      r.get('avg_bitrate').updateValueAndValidity({emitEvent: false});
      // The CRF ceiling is the codec's, so a codec switch can put a rung's CRF out of range.
      r.get('crf').updateValueAndValidity({emitEvent: false});
      r.updateValueAndValidity({emitEvent: false});
    });
    group.updateValueAndValidity({emitEvent: false});
  }

  /**
   * Report an added or removed rendition, after the validity that depends on the list is current.
   *
   * Whether the group needs its own CRF depends on which renditions remain, so the refresh has to
   * come first: the list change is what tells the host control to re-validate, and if it went out
   * before the refresh the host would cache the stale verdict until the next unrelated edit.
   */
  private _announceRenditionChange(group: FormGroup): void {
    this._refreshConditionalValidity(group);
    this.renditionsOf(group).updateValueAndValidity();
  }

  /**
   * Bring the cross-control state up to date after a rebuild.
   *
   * Both halves matter on load, not just on edit: a rendition's validators first run while it is
   * still detached from its group, so `avg_bitrate` cannot see `two_pass` yet and a two-pass group
   * loaded with a blank average would look valid until something else touched it.
   */
  private _syncCodecConstraints(): void {
    this.groups.controls.forEach((group: FormGroup) => {
      const meta = LADDER_CODECS[group.get('codec').value as LadderCodec];
      if (meta && !meta.twoPass) {
        group.get('two_pass').disable({emitEvent: false});
      }
      this._syncCrfEnabled(group);
      this._refreshConditionalValidity(group);
    });
    this.form.updateValueAndValidity({emitEvent: false});
  }

  private _declaredTiers(): DrmStreamType[] {
    return this.drmTiers.controls.map(t => t.get('streamtype').value);
  }

  private _emit(): void {
    this.changed(this._build());
  }

  // ── Serialisation ──────────────────────────────────────────────────────────

  /** Build the `encoding_ladder` object exactly as the template expects to read it. */
  private _build(): EncodingLadder {
    const raw = this.form.getRawValue();
    const audioOnly = !(raw.groups ?? []).length;
    const ladder: EncodingLadder = {
      gop_in_seconds: Number(raw.gop_in_seconds),
      gops_per_segment: Number(raw.gops_per_segment),
      // AUDIO first, then the width tiers the editor holds -- the order the template validates.
      // An audio-only ladder has no video rendition to split by width, so the editor's width
      // tiers (kept around, hidden, in case the operator turns audio-only back off) are left out
      // of what is actually sent -- only the always-present AUDIO tier applies.
      drm_streamtypes: audioOnly ? [{...AUDIO_DRM_TIER}] : [
        {...AUDIO_DRM_TIER},
        ...(raw.drm_streamtypes ?? []).map((t: any) => ({
          streamtype: t.streamtype,
          max_width: numberOrNull(t.max_width),
        })),
      ],
      groups: (raw.groups ?? []).map((g: any) => {
        const group: LadderGroup = {
          codec: g.codec,
          outputs: [...(g.out_dash ? ['dash'] : []), ...(g.out_hls ? ['hls'] : [])] as LadderGroup['outputs'],
          two_pass: !!g.two_pass,
          pixfmt: g.pixfmt,
          renditions: (g.renditions ?? []).map((r: any) => buildRendition({
            width: Number(r.width),
            max_bitrate: r.max_bitrate,
            avg_bitrate: r.avg_bitrate,
            crf: numberOrNull(r.crf) ?? undefined,
            min_bitrate: r.min_bitrate,
            bufsize: r.bufsize,
          })),
        };
        // crf is the quality target of a single-pass encode; a two_pass group targets avg_bitrate
        // and never emits -crf, so sending one would only be noise in the stored PO.
        if (!g.two_pass && numberOrNull(g.crf) !== null) {
          group.crf = Number(g.crf);
        }
        if (g.profile) { group.profile = g.profile; }
        if (g.preset) { group.preset = g.preset; }
        const params = parseEncoderParams(g.encoder_params);
        if (params && Object.keys(params).length) { group.encoder_params = params; }
        return group;
      }),
    };
    if (!ladder.groups.length) {
      // No group to take it from -- an audio-only ladder states outputs once, at the top level.
      ladder.outputs = [
        ...(raw.ladder_out_dash ? ['dash'] : []), ...(raw.ladder_out_hls ? ['hls'] : []),
      ] as EncodingLadder['outputs'];
    }
    // Only when it differs from the template's own policy: leaving the key off keeps a job on
    // whatever the template derives, which has already moved twice under this UI.
    if (audioModeWritesLayouts(raw.audio_mode) && !this.audioRowsAreDefault()) {
      ladder.audio_layouts = this._buildAudioLayouts();
    }
    if (raw.audio_mode === 'custom_renditions' && this.audioRows.length) {
      ladder.audio_renditions = this._buildAudioRenditions();
    }
    if (raw.exceed_input_resolution) {
      ladder.exceed_input_resolution = true;
    }
    return ladder;
  }

  /** Rows for a ladder as loaded: its explicit list, its layout policy, or the mode's defaults. */
  private static _rowsFor(ladder: EncodingLadder): AudioRow[] {
    const mode = EncodingLadderComponent._audioModeOf(ladder);
    if (mode === 'custom_renditions') {
      return (ladder.audio_renditions as LadderAudioRendition[]).map(a => ({
        channels: a.channels ?? null,
        codec: a.codec ?? 'aac',
        bitrate: a.bitrate,
        samplerate: a.samplerate ?? null,
        display: null,
        required: false,
        allow_downmix: false,
      }));
    }
    if (audioModeWritesLayouts(mode) && ladder.audio_layouts?.length) {
      return ladder.audio_layouts.flatMap(l => (l.codecs ?? []).map(entry => ({
        channels: l.channels,
        codec: entry.codec,
        bitrate: entry.bitrate,
        samplerate: null,
        display: l.display ?? null,
        required: !!l.required,
        allow_downmix: !!l.allow_downmix,
      })));
    }
    return defaultAudioRows(mode);
  }

  private static _audioModeOf(ladder: EncodingLadder): AudioMode {
    // A stored explicit list keeps its form: it is a different request from a layout policy, and
    // rewriting it as one would change what every track gets.
    if (Array.isArray(ladder.audio_renditions) && ladder.audio_renditions.length) {
      return 'custom_renditions';
    }
    // An explicit null is the retired "one output per source track". Nothing offers it any more,
    // so a stored one is shown as the policy it is closest to rather than as a mode.
    return ladder.audio_layouts?.length ? 'custom_layouts' : 'passthrough_all';
  }

  /** Regroup the per-codec rows back into one entry per channel count, which is the ladder shape. */
  private _buildAudioLayouts(): LadderAudioLayout[] {
    const byChannels = new Map<number, LadderAudioLayout>();
    for (const row of this.audioRows.getRawValue() as any[]) {
      const channels = Number(row.channels);
      const existing = byChannels.get(channels);
      if (existing) {
        // Same layout, another codec -- that pairing is what emits 5.1 in AAC and E-AC-3, and each
        // codec keeps its own bitrate.
        if (!existing.codecs.some(c => c.codec === row.codec)) {
          existing.codecs.push({codec: row.codec, bitrate: row.bitrate});
        }
        continue;
      }
      byChannels.set(channels, normaliseAudioLayout({
        channels,
        display: row.display || null,
        codecs: [{codec: row.codec, bitrate: row.bitrate}],
        required: !!row.required,
        allow_downmix: !!row.allow_downmix,
      }));
    }
    return [...byChannels.values()].sort((a, b) => a.channels - b.channels);
  }

  private _buildAudioRenditions(): LadderAudioRendition[] {
    return (this.audioRows.getRawValue() as any[]).map(row => {
      const rendition: LadderAudioRendition = {bitrate: row.bitrate};
      if (row.codec) { rendition.codec = row.codec; }
      if (row.samplerate !== null && row.samplerate !== '') { rendition.samplerate = Number(row.samplerate); }
      if (row.channels !== null && row.channels !== '') { rendition.channels = Number(row.channels); }
      return rendition;
    });
  }

  /**
   * Accepts the flat single-group sugar as well as the explicit `groups` list.
   *
   * An explicit empty list is audio-only and has to survive as `[]` -- only an *absent* `groups`
   * key falls back to the flat form or the first preset's group.
   */
  private static _groupsOf(ladder: EncodingLadder): LadderGroup[] {
    if (Array.isArray(ladder.groups)) {
      return ladder.groups;
    }
    const flat = ladder as unknown as LadderGroup;
    return flat.codec ? [flat] : [LADDER_PRESETS[0].ladder.groups[0]];
  }

  private static _matchPreset(ladder: EncodingLadder): string {
    const wanted = stableStringify(ladder);
    return LADDER_PRESETS.find(p => stableStringify(normaliseLadder(p.ladder)) === wanted)?.id ?? CUSTOM_PRESET_ID;
  }
}

// ── Validators ───────────────────────────────────────────────────────────────

function numberOrNull(value: any): number | null {
  return value === null || value === undefined || value === '' ? null : Number(value);
}

const bitratePattern: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = control.value;
  return !value || BITRATE_PATTERN.test(String(value)) ? null : {bitrate: true};
};

/**
 * The group's CRF is the target of every single-pass rung that names none of its own -- so it is
 * required exactly when such a rung exists, as in the template. A ladder whose every rung states
 * its own crf or avg_bitrate (the uncapped Bandwidth Saver AV1 group) needs none.
 */
const crfRequiredUnlessTwoPass: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const group = control.parent;
  if (!group || group.get('two_pass')?.value) {
    return null;
  }
  const rungs = (group.get('renditions') as FormArray | null)?.controls ?? [];
  const inherits = rungs.some(r => numberOrNull(r.get('crf').value) === null && !usableAvgBitrate(r));
  return inherits && numberOrNull(control.value) === null ? {required: true} : null;
};

/**
 * CRF within the encoder's own range (`LADDER_CODECS[codec].crfMax`): 51 for x264/x265, 63 for
 * SVT-AV1. Sits on both the group's CRF and each rung's, so the codec is found by walking up to
 * whichever ancestor holds it -- the group directly, or rendition -> renditions -> group.
 */
const crfWithinCodecRange: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const crf = numberOrNull(control.value);
  if (crf === null) {
    return null;
  }
  const meta = codecMetaOf(control);
  return meta && crf > meta.crfMax ? {max: {max: meta.crfMax, actual: crf}} : null;
};

/** The codec facts of the group a control sits in, found by walking up to the ancestor holding `codec`. */
function codecMetaOf(control: AbstractControl): (typeof LADDER_CODECS)[LadderCodec] | undefined {
  let owner = control.parent;
  while (owner && !owner.get('codec')) {
    owner = owner.parent;
  }
  return LADDER_CODECS[owner?.get('codec').value as LadderCodec];
}

/**
 * `avg_bitrate` is a VBR target, and the template drives VBR only through the codecs it can run
 * two-pass (`twoPass`). SVT-AV1 has no such path, so the template refuses `avg_bitrate` on every
 * AV1 rung -- capped or not -- and an AV1 rung's only target of its own is a CRF.
 */
const avgBitrateNeedsVbr: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const meta = codecMetaOf(control);
  return control.value && meta && !meta.twoPass ? {avgUnsupported: true} : null;
};

/**
 * Whether a rung's average bitrate counts as its own target: stated, on a codec that has one.
 * A rung not yet attached to its group has no codec to ask; it is taken at its word until
 * `_refreshConditionalValidity` re-runs it in place, rather than flagged for a codec it may not have.
 */
function usableAvgBitrate(rendition: AbstractControl): boolean {
  return !!rendition.get('avg_bitrate').value && (codecMetaOf(rendition.get('avg_bitrate'))?.twoPass ?? true);
}

const avgBitrateRequiredWhenTwoPass: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  // avg_bitrate -> rendition group -> renditions array -> the codec group holding two_pass.
  const group = control.parent?.parent?.parent;
  if (!group || !group.get('two_pass')?.value) {
    return null;
  }
  // A rung stating its own crf stays a single CRF pass inside a two-pass group.
  if (numberOrNull(control.parent.get('crf')?.value) !== null) {
    return null;
  }
  return control.value ? null : {required: true};
};

/**
 * The rate-control keys of one rung have to agree, exactly as the template checks them: crf and
 * avg_bitrate are alternatives, and a rung without max_bitrate is uncapped -- so it must name its
 * own target, and bufsize/min_bitrate (VBV settings) have nothing to act on.
 */
const renditionRateValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const value = (name: string) => control.get(name).value;
  const hasCrf = numberOrNull(value('crf')) !== null;
  if (hasCrf && value('avg_bitrate')) {
    return {crfAndAvg: true};
  }
  if (value('max_bitrate')) {
    return null;
  }
  // Both are reported when both hold: a bufsize typed into an uncapped rung is usually the real
  // cause (the cap was meant to be there), and showing only the missing target would hide it.
  const errors: ValidationErrors = {};
  if (value('bufsize') || value('min_bitrate')) {
    errors.vbvWithoutCap = true;
  }
  if (!hasCrf && !usableAvgBitrate(control)) {
    errors.uncappedWithoutTarget = true;
  }
  return Object.keys(errors).length ? errors : null;
};

const encoderParamsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const params = parseEncoderParams(control.value);
  if (params === null) {
    return {encoderParamsSyntax: true};
  }
  const meta = LADDER_CODECS[control.parent?.get('codec')?.value as LadderCodec];
  if (!meta || !Object.keys(params).length) {
    return null;
  }
  if (!meta.encoderParams) {
    return {encoderParamsUnsupported: true};
  }
  // On the name the encoder resolves, or another spelling (`open_gop`, `no-open-gop`) gets through.
  const reserved = Object.keys(params).sort().filter(k => meta.reservedParams.includes(meta.paramName(k)))
    .map(k => meta.paramName(k) === k ? k : `${k} (read as ${meta.paramName(k)})`);
  return reserved.length ? {encoderParamsReserved: reserved.join(', ')} : null;
};

/** One rung in the shape the template reads: only the keys it states, `max_bitrate` included. */
function buildRendition(r: LadderRendition): LadderRendition {
  const rendition: LadderRendition = {width: r.width};
  if (r.max_bitrate) { rendition.max_bitrate = r.max_bitrate; }
  if (r.avg_bitrate) { rendition.avg_bitrate = r.avg_bitrate; }
  if (r.crf !== null && r.crf !== undefined) { rendition.crf = Number(r.crf); }
  if (r.min_bitrate) { rendition.min_bitrate = r.min_bitrate; }
  if (r.bufsize) { rendition.bufsize = r.bufsize; }
  return rendition;
}

const groupValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const group = control as FormGroup;
  const errors: ValidationErrors = {};
  if (!group.get('out_dash').value && !group.get('out_hls').value) {
    errors['noOutputs'] = true;
  }
  const codec = group.get('codec').value as LadderCodec;
  if (group.get('two_pass').value && !LADDER_CODECS[codec]?.twoPass) {
    errors['twoPassUnsupported'] = codec;
  }
  // A profile too shallow for the pixel format does not degrade -- the encoder refuses to open, and
  // the template checks it for that reason. Caught here it costs a click; caught there it costs
  // however many hours the job had already run.
  const profile = group.get('profile').value as string | null;
  const pixfmt = group.get('pixfmt').value as string;
  if (!profileCarriesPixfmt(codec, profile, pixfmt)) {
    errors['profileTooShallow'] = `${profile} cannot carry ${pixfmt}`;
  }
  return Object.keys(errors).length ? errors : null;
};

/**
 * An empty list is not "nothing configured yet" -- the only way to reach one is the dedicated
 * "Audio only" toggle (`setAudioOnly`; the last group's remove button is hidden), so it always
 * means an explicit audio-only ladder. It is valid provided the ladder-level outputs it now has to
 * carry -- there being no group left to carry its own -- name at least one manifest.
 */
const groupsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const groups = control as FormArray;
  if (!groups.length) {
    const parent = control.parent;
    const dash = parent?.get('ladder_out_dash')?.value;
    const hls = parent?.get('ladder_out_hls')?.value;
    return dash || hls ? null : {noAudioOutputs: true};
  }
  const codecs = groups.controls.map(g => g.get('codec').value);
  const duplicate = codecs.find((c, i) => codecs.indexOf(c) !== i);
  return duplicate ? {duplicateCodec: duplicate} : null;
};

/**
 * One table, so one validator -- but the rules differ by mode, because the modes write different
 * keys. The mode is read off the parent form rather than passed in, since a FormArray validator
 * only sees its own control.
 */
const audioRowsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const rows = (control as FormArray).controls;
  const mode = control.parent?.get('audio_mode')?.value as AudioMode | undefined;
  if (!mode) {
    return null;
  }
  if (!rows.length) {
    return {noRows: true};
  }
  const value = (r: AbstractControl, name: string) => r.get(name).value;

  if (audioModeWritesLayouts(mode)) {
    // A (layout, codec) pair is one rendition; repeating it would emit the same file twice.
    const pairs = rows.map(r => `${value(r, 'channels')}/${value(r, 'codec')}`);
    const duplicate = pairs.find((p, i) => pairs.indexOf(p) !== i);
    if (duplicate) {
      return {duplicatePair: duplicate.replace('/', ' in ')};
    }
    // Rows sharing a channel count are one layout, so their layout-level fields must agree.
    for (const row of rows) {
      const sibling = rows.find(o => o !== row && value(o, 'channels') === value(row, 'channels'));
      // Not bitrate: each codec of a layout is priced for what it actually needs.
      if (sibling && ['required', 'allow_downmix', 'display']
        .some(f => value(sibling, f) !== value(row, f))) {
        return {inconsistentLayout: value(row, 'channels')};
      }
    }
    const stereoOnly = rows.find(r => !audioCodecAllowedAt(value(r, 'codec'), numberOrNull(value(r, 'channels'))));
    if (stereoOnly) {
      return {stereoOnlyCodec: AUDIO_CODEC_LABELS[value(stereoOnly, 'codec')] ?? value(stereoOnly, 'codec')};
    }
    return rows.some(r => value(r, 'required')) ? null : {noRequiredLayout: true};
  }

  const stereoOnly = rows.find(r => !audioCodecAllowedAt(value(r, 'codec'), numberOrNull(value(r, 'channels'))));
  if (stereoOnly) {
    return {stereoOnlyCodec: AUDIO_CODEC_LABELS[value(stereoOnly, 'codec')] ?? value(stereoOnly, 'codec')};
  }
  // custom: codec, channels and bitrate together name the output file.
  const identities = rows.map(r => JSON.stringify(
    [value(r, 'codec'), value(r, 'channels'), value(r, 'bitrate'), value(r, 'samplerate')]));
  const duplicate = identities.find((id, i) => identities.indexOf(id) !== i);
  return duplicate ? {duplicateRendition: JSON.parse(duplicate)[2]} : null;
};

/**
 * The tiers a rendition width maps onto. Enforces exactly what the template does: known types,
 * no repeats, declared in the canonical order, strictly ascending bounds, and a final unbounded
 * tier so no width falls through. A rendition encrypted under the wrong tier's key does not play,
 * and nothing downstream reports it -- which is why this is checked rather than coerced.
 *
 * An audio-only ladder (empty `groups`, read off the sibling control) has no video rendition to
 * split by width at all, so zero tiers is valid there -- the width-tier editor is hidden while
 * audio-only, but a ladder loaded from a stored PO or an import may still arrive with none.
 */
const drmTiersValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const tiers = (control as FormArray).controls.map(t => ({
    streamtype: t.get('streamtype').value as DrmStreamType,
    max_width: numberOrNull(t.get('max_width').value),
  }));
  if (!tiers.length) {
    const groups = control.parent?.get('groups') as FormArray | undefined;
    return groups && groups.length === 0 ? null : {noTiers: true};
  }
  const names = tiers.map(t => t.streamtype);
  if (names.some((n, i) => names.indexOf(n) !== i)) {
    return {duplicateTier: true};
  }
  const order = names.map(n => DRM_STREAMTYPES.indexOf(n));
  if (order.some((o, i) => i > 0 && o <= order[i - 1])) {
    return {tierOrder: true};
  }
  if (tiers[tiers.length - 1].max_width !== null) {
    return {lastTierBounded: true};
  }
  const bounded = tiers.slice(0, -1).map(t => t.max_width);
  if (bounded.some(w => w === null)) {
    return {tierUnbounded: true};
  }
  if (bounded.some((w, i) => i > 0 && w <= bounded[i - 1])) {
    return {tierAscending: true};
  }
  return null;
};

/**
 * Put a ladder into exactly the shape the editor emits.
 *
 * Two callers, for the same reason. Preset matching needs both sides in one shape, because a
 * preset is written the way the template reads it -- with every key it is content to inherit left
 * off. And the submit path needs it because the card is a ControlValueAccessor whose `writeValue`
 * does not emit: an operator who never touches it leaves the raw preset object sitting in the PO
 * form control, and that object is what gets submitted. The first preset omits
 * `gops_per_segment`, so the segment length of every untouched PO was left to the template
 * instead of being stated by the job that ran -- and the stored PO is the only record of it.
 */
export function normaliseLadder(ladder: EncodingLadder): EncodingLadder {
  // The template also accepts a single group spelled flat at the top level (codec, renditions,
  // outputs); the editor reads it as one group (_groupsOf), so normalisation must too.
  if (!Array.isArray(ladder.groups) && (ladder as unknown as LadderGroup).codec) {
    ladder = {...ladder, groups: [ladder as unknown as LadderGroup]};
  }
  const audioOnly = !(ladder.groups ?? []).length;
  return {
    gop_in_seconds: ladder.gop_in_seconds ?? DEFAULT_GOP_IN_SECONDS,
    gops_per_segment: ladder.gops_per_segment ?? DEFAULT_GOPS_PER_SEGMENT,
    // No video rendition to split by width once audio-only -- any width tiers a stored or
    // imported ladder still carried are dropped, matching what _build() emits.
    drm_streamtypes: audioOnly ? [{...AUDIO_DRM_TIER}] : [
      {...AUDIO_DRM_TIER},
      ...(ladder.drm_streamtypes ?? DEFAULT_DRM_STREAMTYPES)
        .filter(t => t.streamtype !== AUDIO_DRM_TIER.streamtype)
        .map(t => ({streamtype: t.streamtype, max_width: t.max_width ?? null})),
    ],
    groups: (ladder.groups ?? []).map(g => {
      const group: LadderGroup = {
        codec: g.codec,
        outputs: [...(g.outputs.includes('dash') ? ['dash'] : []), ...(g.outputs.includes('hls') ? ['hls'] : [])] as LadderGroup['outputs'],
        two_pass: !!g.two_pass,
        pixfmt: g.pixfmt ?? LADDER_CODECS[g.codec]?.defaultPixfmt ?? 'yuv420p',
        renditions: g.renditions.map(buildRendition),
      };
      if (!g.two_pass && g.crf !== null && g.crf !== undefined) { group.crf = g.crf; }
      if (g.profile) { group.profile = g.profile; }
      if (g.preset) { group.preset = g.preset; }
      if (g.encoder_params && Object.keys(g.encoder_params).length) { group.encoder_params = {...g.encoder_params}; }
      return group;
    }),
    // Audio-only: no group left to carry it, so it is stated once, at the top level, in the
    // template's canonical order -- the same shape _build() emits.
    ...((ladder.groups ?? []).length
      ? {}
      : {outputs: LADDER_OUTPUTS.filter(o => (ladder.outputs ?? []).includes(o))}),
    // A preset's own layout policy has to survive the round trip, or selecting it would emit
    // audio_layouts the comparison never saw and the dropdown would fall to "Custom" on the next
    // form event. Only a policy that differs from the template's is emitted, matching _build().
    ...(ladder.audio_layouts?.length
      && stableStringify(ladder.audio_layouts.map(normaliseAudioLayout))
        !== stableStringify(AUTO_AUDIO_LAYOUTS.map(normaliseAudioLayout))
      ? {audio_layouts: [...ladder.audio_layouts].map(normaliseAudioLayout)
          .sort((a, b) => a.channels - b.channels)}
      : {}),
    ...(ladder.audio_renditions === null ? {audio_renditions: null} : {}),
    ...(Array.isArray(ladder.audio_renditions) && ladder.audio_renditions.length
      ? {audio_renditions: ladder.audio_renditions}
      : {}),
    ...(ladder.exceed_input_resolution ? {exceed_input_resolution: true} : {}),
  };
}
